/**
 * Shared helpers to build commission invoice line-description context
 * (student / course / dates) for create + edit flows.
 */

export function pickClientDisplayName(clientRecord) {
  if (!clientRecord || typeof clientRecord !== "object") return "";
  return (
    clientRecord.full_name ||
    clientRecord.fullName ||
    clientRecord.clientname ||
    clientRecord.client_name ||
    clientRecord.name ||
    ""
  );
}

export function pickProviderDisplayName(planPayload) {
  const raw = planPayload?.raw;
  if (!raw || typeof raw !== "object") return "";
  return String(
    raw?.provider?.name ||
      raw.provider_name ||
      raw.providerName ||
      raw.providername ||
      raw?.application?.provider_name ||
      raw?.application?.providername ||
      "",
  ).trim();
}

export function pickClientDob(clientRecord) {
  if (!clientRecord || typeof clientRecord !== "object") return "";
  return (
    clientRecord?.other_details?.dob ||
    clientRecord.dob ||
    clientRecord.date_of_birth ||
    clientRecord.birth_date ||
    ""
  );
}

/** Student ID from client record — never fall back to internal client id. */
export function pickClientStudentId(clientRecord) {
  if (!clientRecord || typeof clientRecord !== "object") return "";
  return String(
    clientRecord.student_id ||
      clientRecord.studentId ||
      clientRecord?.other_details?.student_id ||
      "",
  ).trim();
}

/**
 * Map commission detail view-model fields into suggestion meta.
 */
export function suggestionMetaFromViewModel(viewModel = {}, extras = {}) {
  return {
    courseName:
      viewModel?.course?.name ||
      viewModel?.inquiry?.course ||
      extras.courseName ||
      "",
    providerName:
      viewModel?.provider?.name ||
      viewModel?.inquiry?.providerName ||
      extras.providerName ||
      "",
    studentName:
      viewModel?.student?.name ||
      extras.studentName ||
      "",
    studentId:
      viewModel?.student?.studentId ||
      viewModel?.inquiry?.studentId ||
      extras.studentId ||
      "",
    studentDob:
      viewModel?.student?.birthDate ||
      viewModel?.inquiry?.studentBirthDate ||
      extras.studentDob ||
      "",
    startDate:
      viewModel?.course?.startDate ||
      viewModel?.inquiry?.start ||
      extras.startDate ||
      "",
    endDate:
      viewModel?.course?.endDate ||
      viewModel?.inquiry?.end ||
      extras.endDate ||
      "",
    commissionRate:
      extras.commissionRate ||
      viewModel?.commissionPlan?.existing?.commissionRate ||
      viewModel?.commissionPlan?.defaults?.commissionRate ||
      "",
    commissionType:
      extras.commissionType ||
      viewModel?.commissionPlan?.existing?.commissionType ||
      "percentage",
    providerId:
      extras.providerId ||
      viewModel?.provider?.id ||
      "",
    courseId:
      extras.courseId ||
      viewModel?.course?.id ||
      "",
    applicationCourseId:
      extras.applicationCourseId ||
      viewModel?.commissionPlan?.defaults?.applicationCourseId ||
      viewModel?.application?.applicationCourseId ||
      viewModel?.application?.application_course_id ||
      viewModel?.course?.applicationCourseId ||
      viewModel?.course?.application_course_id ||
      extras.courseId ||
      viewModel?.course?.id ||
      "",
    applicationId:
      extras.applicationId ||
      viewModel?.application?.id ||
      null,
    applicationType:
      extras.applicationType ||
      viewModel?.application?.type ||
      "education",
  };
}

/** Pull commission detail ids from returnTo / search query. */
export function parseCommissionDetailIdsFromUrl(searchOrReturnTo = "") {
  try {
    let qs = String(searchOrReturnTo || "");
    if (qs.includes("?")) qs = qs.split("?")[1] || "";
    const params = new URLSearchParams(qs);
    return {
      applicationId:
        params.get("app_id") ||
        params.get("appId") ||
        params.get("application_id") ||
        null,
      applicationType: params.get("type") || params.get("application_type") || "",
      providerId:
        params.get("provider_id") || params.get("providerId") || "",
      courseId: params.get("course_id") || params.get("courseId") || "",
      applicationCourseId:
        params.get("application_course_id") ||
        params.get("applicationCourseId") ||
        params.get("course_id") ||
        params.get("courseId") ||
        "",
      planId: params.get("planId") || params.get("plan_id") || "",
      studentId:
        params.get("student_id") ||
        params.get("studentId") ||
        params.get("client_id") ||
        "",
    };
  } catch {
    return {
      applicationId: null,
      applicationType: "",
      providerId: "",
      courseId: "",
      applicationCourseId: "",
      planId: "",
      studentId: "",
    };
  }
}
